<template>
  <div class="hr">
    <!-- Hero Section -->
    <PageHero 
      :title="$t('hr.hero.title')"
      :subtitle="$t('hr.hero.subtitle')"
      :description="$t('hr.hero.description')"
      :showCta="true"
      :ctaText="$t('common.tryNow')"
    />

    <!-- Key Benefits Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6">{{ $t('hr.benefits.title') }}</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Open Source -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-6">
              <i class="fas fa-code-branch text-[#faf6df] text-xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.benefits.cards.openSource.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('hr.benefits.cards.openSource.description') }}
            </p>
          </div>

          <!-- Cost Effective -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-6">
              <i class="fas fa-dollar-sign text-[#faf6df] text-xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.benefits.cards.costEffective.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('hr.benefits.cards.costEffective.description') }}
            </p>
          </div>

          <!-- Customizable -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-6">
              <i class="fas fa-cogs text-[#faf6df] text-xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.benefits.cards.customizable.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('hr.benefits.cards.customizable.description') }}
            </p>
          </div>

          <!-- Easy Integration -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-6">
              <i class="fas fa-plug text-[#faf6df] text-xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.benefits.cards.easyIntegration.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('hr.benefits.cards.easyIntegration.description') }}
            </p>
          </div>

          <!-- Advanced System -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-6">
              <i class="fas fa-code text-[#faf6df] text-xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.benefits.cards.advanced.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('hr.benefits.cards.advanced.description') }}
            </p>
          </div>

          <!-- Integrated Solution -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-6">
              <i class="fas fa-th text-[#faf6df] text-xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.benefits.cards.integrated.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('hr.benefits.cards.integrated.description') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6">{{ $t('hr.features.title') }}</h2>
          <p class="text-xl text-gray-600">{{ $t('hr.features.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Recruitment -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-user-plus text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.recruitment.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.recruitment.description') }}</p>
          </div>

          <!-- Employee Lifecycle -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-users-cog text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.lifecycle.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.lifecycle.description') }}</p>
          </div>

          <!-- Attendance -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-clock text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.attendance.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.attendance.description') }}</p>
          </div>

          <!-- Leave Management -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-calendar-alt text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.leave.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.leave.description') }}</p>
          </div>

          <!-- Expense Management -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-receipt text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.expense.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.expense.description') }}</p>
          </div>

          <!-- Performance -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-chart-line text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.performance.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.performance.description') }}</p>
          </div>

          <!-- Payroll -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-money-check-alt text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.payroll.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.payroll.description') }}</p>
          </div>

          <!-- Tax Reports -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-file-invoice-dollar text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.tax.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.tax.description') }}</p>
          </div>

          <!-- Mobile App -->
          <div class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div class="w-24 h-24 mx-auto bg-[#0f4640] rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-all duration-300 hover:bg-[#1f9285]">
              <i class="fas fa-mobile-alt text-[#faf6df] text-4xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('hr.features.cards.mobile.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('hr.features.cards.mobile.description') }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'HRView',
  components: {
    PageHero
  }
}
</script>

<style scoped>
.hr {
  overflow-x: hidden;
}

/* Add smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Add hover effects */
.hover\:shadow-xl:hover {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Add animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}
</style> 